// src/client/renderer/vfx/items/thunder-fist.ts
//
// Thunder Fist item VFX.
//
// Event phases:
//   item_cast   -> phase: 'cast'   (caster-only charge flare)
//   item_hit    -> handled by the board cinematic in main.ts
//   item_missed -> phase: 'impact' (full electric fist/beam along aimed direction)

import * as THREE from 'three';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import type { GameEvent, ItemCastEvent, ItemMissedEvent } from '../../../../shared/game/types';
import { disposeSkillshotGroup } from './skillshot-shared';

const MAX_ACTIVE = 4;
const BEAM_COLOUR = 0xfff099;
const CORE_COLOUR = 0xffffff;
const CHARGE_LIFETIME = 0.55;
const BEAM_APPEAR_DURATION = 0.08;
const BEAM_HOLD_DURATION = 0.36;
const BEAM_FADE_DURATION = 0.18;
const BEAM_LIFETIME = BEAM_APPEAR_DURATION + BEAM_HOLD_DURATION + BEAM_FADE_DURATION;
const BEAM_SPARK_SCALE_END = 2.25;
const MISS_BEAM_LENGTH = 3.4;
const NUM_SEGMENTS = 5;
const PROJECTILE_TRAVEL_DURATION = 0.38;
const PROJECTILE_IMPACT_HOLD_DURATION = 0.12;
const PROJECTILE_HIDE_AT = PROJECTILE_TRAVEL_DURATION + PROJECTILE_IMPACT_HOLD_DURATION;
const IMPACT_BURST_START = PROJECTILE_TRAVEL_DURATION - 0.04;
const PROJECTILE_MODEL_SCALE = 1.25;
const FALLBACK_FIST_SCALE = 2.3;
const FORWARD = new THREE.Vector3(0, 0, 1);

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

function buildJaggedPoints(
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
): THREE.Vector3[] {
  const points: THREE.Vector3[] = [];
  const vFrom = new THREE.Vector3(from.x, from.y, from.z);
  const vTo = new THREE.Vector3(to.x, to.y, to.z);
  const dir = new THREE.Vector3().subVectors(vTo, vFrom);
  const dirXZ = new THREE.Vector3(dir.x, 0, dir.z);
  const up = new THREE.Vector3(0, 1, 0);
  const perp =
    dirXZ.lengthSq() < 1e-8
      ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3().crossVectors(dirXZ.normalize(), up).normalize();

  for (let i = 0; i <= NUM_SEGMENTS; i++) {
    const t = i / NUM_SEGMENTS;
    const pt = new THREE.Vector3().lerpVectors(vFrom, vTo, t);
    if (i > 0 && i < NUM_SEGMENTS) {
      const r = (Math.random() - 0.5) * 0.55;
      pt.x += r * perp.x;
      pt.z += r * perp.z;
    }
    points.push(pt);
  }
  return points;
}

function buildLineSegmentsFromPoints(
  points: THREE.Vector3[],
  material: THREE.LineBasicMaterial,
): THREE.LineSegments {
  const pairs: THREE.Vector3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    pairs.push(points[i].clone(), points[i + 1].clone());
  }
  return new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pairs), material);
}

function markSharedResourceClone(object: THREE.Object3D): void {
  object.traverse((child) => {
    child.userData.preserveSharedResources = true;
    if (child instanceof THREE.Mesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
}

function buildProceduralFallbackFist(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'thunder-fist-procedural-fallback';

  const material = new THREE.MeshBasicMaterial({
    color: CORE_COLOUR,
    transparent: true,
    opacity: 0.95,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });

  const palm = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.28, 0.28), material);
  palm.position.set(0, 0, -0.02);
  group.add(palm);

  const knuckleGeometry = new THREE.BoxGeometry(0.095, 0.11, 0.18);
  for (let index = 0; index < 4; index += 1) {
    const knuckle = new THREE.Mesh(knuckleGeometry, material);
    knuckle.position.set((index - 1.5) * 0.095, 0.08, 0.16);
    group.add(knuckle);
  }

  const thumb = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.24), material);
  thumb.position.set(0.23, -0.03, 0.02);
  thumb.rotation.z = -0.65;
  group.add(thumb);
  group.scale.setScalar(FALLBACK_FIST_SCALE);

  return group;
}

function alignDominantAxisToForward(object: THREE.Object3D): void {
  object.updateWorldMatrix(true, true);
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) return;
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  object.position.sub(center);

  const maxAxis = Math.max(size.x, size.y, size.z);
  let axis: THREE.Vector3;
  if (size.y >= maxAxis * 0.72) {
    axis = new THREE.Vector3(0, 1, 0);
  } else if (size.x >= size.y && size.x >= size.z) {
    axis = new THREE.Vector3(1, 0, 0);
  } else if (size.y >= size.x && size.y >= size.z) {
    axis = new THREE.Vector3(0, 1, 0);
  } else {
    axis = new THREE.Vector3(0, 0, 1);
  }
  const alignment = new THREE.Quaternion().setFromUnitVectors(axis, FORWARD);
  object.quaternion.premultiply(alignment);
}

export function orientThunderFistProjectile(
  object: THREE.Object3D,
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
): void {
  const direction = new THREE.Vector3(to.x - from.x, to.y - from.y, to.z - from.z);
  if (direction.lengthSq() < 1e-8) return;
  object.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(FORWARD, direction.normalize()));
}

export function createThunderFistProjectileObject(
  modelScene?: THREE.Object3D | null,
): THREE.Object3D {
  if (modelScene != null) {
    const wrapper = new THREE.Group();
    wrapper.name = 'thunder-fist-model-projectile';
    const clone = modelScene.clone(true);
    clone.name = 'thunder-fist-model-projectile-model';
    markSharedResourceClone(clone);
    alignDominantAxisToForward(clone);
    wrapper.add(clone);
    wrapper.scale.setScalar(PROJECTILE_MODEL_SCALE);
    return wrapper;
  }
  return buildProceduralFallbackFist();
}

function resolveDirectionEndpoint(
  from: { x: number; y: number; z: number },
  direction: { dx: number; dy: number } | undefined,
): { x: number; y: number; z: number } {
  if (direction == null) return { x: from.x + MISS_BEAM_LENGTH, y: from.y, z: from.z };
  const magnitude = Math.hypot(direction.dx, direction.dy);
  if (!Number.isFinite(magnitude) || magnitude < 0.001) {
    return { x: from.x + MISS_BEAM_LENGTH, y: from.y, z: from.z };
  }
  return {
    x: from.x + (direction.dx / magnitude) * MISS_BEAM_LENGTH,
    y: from.y,
    z: from.z + (direction.dy / magnitude) * MISS_BEAM_LENGTH,
  };
}

function buildChargeEmitter(
  casterPos: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const group = new THREE.Group();

  const glowMaterial = new THREE.SpriteMaterial({
    color: BEAM_COLOUR,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const glow = new THREE.Sprite(glowMaterial);
  glow.position.set(casterPos.x, casterPos.y + 0.35, casterPos.z);
  glow.scale.set(0.6, 0.6, 1);
  group.add(glow);

  const ringMaterial = new THREE.MeshBasicMaterial({
    color: BEAM_COLOUR,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.38, 0.58, 48), ringMaterial);
  ring.position.set(casterPos.x, casterPos.y + 0.04, casterPos.z);
  ring.rotation.x = -Math.PI / 2;
  ring.scale.setScalar(0.4);
  group.add(ring);

  const handle = deps.registry.create('thunder_fist_charge');
  state.handle = handle;
  const opacity = { value: 0 };
  handle.timeline.to(
    opacity,
    {
      value: 1,
      duration: 0.12,
      ease: 'power2.out',
      onUpdate: () => {
        glowMaterial.opacity = opacity.value;
        ringMaterial.opacity = opacity.value * 0.85;
      },
    },
    0,
  );
  handle.timeline.to(
    glow.scale,
    { x: 1.8, y: 1.8, z: 1, duration: CHARGE_LIFETIME, ease: 'power2.out' },
    0,
  );
  handle.timeline.to(
    ring.scale,
    { x: 1.65, y: 1.65, z: 1, duration: CHARGE_LIFETIME, ease: 'power2.out' },
    0,
  );
  handle.timeline.to(
    opacity,
    {
      value: 0,
      duration: 0.22,
      ease: 'power1.in',
      onUpdate: () => {
        glowMaterial.opacity = opacity.value;
        ringMaterial.opacity = opacity.value * 0.85;
      },
    },
    CHARGE_LIFETIME - 0.22,
  );
  handle.timeline.call(
    () => {
      state.finished = true;
    },
    undefined,
    CHARGE_LIFETIME,
  );

  return group;
}

function buildBeamEmitter(
  from: { x: number; y: number; z: number },
  to: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
  modelScene?: THREE.Object3D | null,
): THREE.Group {
  const group = new THREE.Group();

  const outerMaterial = new THREE.LineBasicMaterial({
    color: BEAM_COLOUR,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const coreMaterial = new THREE.LineBasicMaterial({
    color: CORE_COLOUR,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  group.add(buildLineSegmentsFromPoints(buildJaggedPoints(from, to), outerMaterial));
  group.add(buildLineSegmentsFromPoints(buildJaggedPoints(from, to), coreMaterial));

  const fist = createThunderFistProjectileObject(modelScene);
  fist.position.set(from.x, from.y, from.z);
  orientThunderFistProjectile(fist, from, to);
  group.add(fist);

  const sparkMaterial = new THREE.SpriteMaterial({
    color: BEAM_COLOUR,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const spark = new THREE.Sprite(sparkMaterial);
  spark.position.set(to.x, to.y, to.z);
  spark.scale.set(0, 0, 1);
  group.add(spark);

  const ringMaterial = new THREE.MeshBasicMaterial({
    color: BEAM_COLOUR,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.2, 0.42, 40), ringMaterial);
  ring.position.set(to.x, to.y + 0.02, to.z);
  ring.rotation.x = -Math.PI / 2;
  ring.scale.setScalar(0.25);
  group.add(ring);

  const handle = deps.registry.create('thunder_fist_beam');
  state.handle = handle;
  const beamOpacity = { value: 0 };
  handle.timeline.to(
    beamOpacity,
    {
      value: 1,
      duration: BEAM_APPEAR_DURATION,
      ease: 'power2.in',
      onUpdate: () => {
        outerMaterial.opacity = beamOpacity.value * 0.45;
        coreMaterial.opacity = beamOpacity.value * 0.55;
      },
    },
    0,
  );
  handle.timeline.to(
    beamOpacity,
    {
      value: 0,
      duration: BEAM_FADE_DURATION,
      ease: 'power2.out',
      onUpdate: () => {
        outerMaterial.opacity = beamOpacity.value * 0.45;
        coreMaterial.opacity = beamOpacity.value * 0.55;
      },
    },
    BEAM_APPEAR_DURATION + BEAM_HOLD_DURATION,
  );
  handle.timeline.to(
    fist.position,
    {
      x: to.x,
      y: to.y,
      z: to.z,
      duration: PROJECTILE_TRAVEL_DURATION,
      ease: 'power2.in',
    },
    0,
  );
  const initialScale = { x: fist.scale.x, y: fist.scale.y, z: fist.scale.z };
  handle.timeline.to(
    fist.scale,
    {
      x: initialScale.x * 1.18,
      y: initialScale.y * 1.18,
      z: initialScale.z * 1.18,
      duration: 0.16,
      ease: 'back.out(2)',
    },
    PROJECTILE_TRAVEL_DURATION,
  );
  handle.timeline.to(
    fist.scale,
    {
      x: initialScale.x * 1.05,
      y: initialScale.y * 1.05,
      z: initialScale.z * 1.05,
      duration: Math.max(0.01, PROJECTILE_HIDE_AT - PROJECTILE_TRAVEL_DURATION - 0.08),
      ease: 'power1.out',
    },
    PROJECTILE_TRAVEL_DURATION + 0.08,
  );
  handle.timeline.to(
    spark.scale,
    {
      x: BEAM_SPARK_SCALE_END,
      y: BEAM_SPARK_SCALE_END,
      z: 1,
      duration: BEAM_LIFETIME - IMPACT_BURST_START,
      ease: 'power2.out',
    },
    IMPACT_BURST_START,
  );
  handle.timeline.to(
    ring.scale,
    { x: 2.2, y: 2.2, z: 1, duration: 0.28, ease: 'power2.out' },
    IMPACT_BURST_START,
  );

  const burstOpacity = { value: 0 };
  handle.timeline.to(
    burstOpacity,
    {
      value: 1,
      duration: 0.1,
      ease: 'power2.out',
      onUpdate: () => {
        sparkMaterial.opacity = burstOpacity.value;
        ringMaterial.opacity = burstOpacity.value * 0.75;
      },
    },
    IMPACT_BURST_START,
  );
  handle.timeline.call(
    () => {
      fist.visible = false;
    },
    undefined,
    PROJECTILE_HIDE_AT,
  );
  handle.timeline.to(
    burstOpacity,
    {
      value: 0,
      duration: BEAM_LIFETIME - PROJECTILE_HIDE_AT,
      ease: 'power2.in',
      onUpdate: () => {
        sparkMaterial.opacity = burstOpacity.value;
        ringMaterial.opacity = burstOpacity.value * 0.75;
      },
    },
    PROJECTILE_HIDE_AT,
  );
  handle.timeline.call(
    () => {
      state.finished = true;
    },
    undefined,
    BEAM_LIFETIME,
  );

  return group;
}

export function createThunderFistPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;
  const emitterStates = new Map<THREE.Object3D, EmitterState>();
  let disposed = false;

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;
    if (emitterStates.size >= MAX_ACTIVE) return null;

    const state: EmitterState = { handle: null!, finished: false };
    let group: THREE.Group;

    if (args.phase === 'cast') {
      if (args.casterPosition == null) return null;
      group = buildChargeEmitter(args.casterPosition, deps, state);
    } else {
      if (args.casterPosition == null && args.targetPosition == null) return null;
      const from = args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const to = args.targetPosition ?? resolveDirectionEndpoint(from, args.direction);
      group = buildBeamEmitter(from, to, deps, state, args.modelScene);
    }

    scene.add(group);
    emitterStates.set(group, state);
    return group;
  }

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  function isFinished(emitter: THREE.Object3D): boolean {
    return emitterStates.get(emitter)?.finished ?? true;
  }

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return;
    state.handle.dispose();
    emitterStates.delete(emitter);
    scene.remove(emitter);
    disposeSkillshotGroup(emitter);
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const [emitter, state] of emitterStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    emitterStates.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

export function resolveThunderFistForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
  getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined,
  getItemModelScene?: (itemId: string) => THREE.Object3D | null,
): { poolId: 'thunder_fist'; args: ItemVfxSpawnArgs } | null {
  if (event.type !== 'item_cast' && event.type !== 'item_missed') {
    return null;
  }
  if (event.itemId !== 'thunder_fist') return null;

  let casterId: string;
  let targetId: string | undefined;
  let toNodeId: string | undefined;
  let direction: { dx: number; dy: number } | undefined;

  if (event.type === 'item_cast') {
    const cast = event as ItemCastEvent;
    casterId = cast.casterId;
    targetId = cast.targetId;
    direction = cast.direction;
  } else {
    const missed = event as ItemMissedEvent;
    casterId = missed.casterId;
    targetId = missed.targetId;
    toNodeId = missed.toNodeId;
    direction = missed.direction;
  }

  const casterPosition =
    casterId && getPositionForPlayer ? getPositionForPlayer(casterId) : undefined;
  const targetPosition =
    (toNodeId && getPositionForNode ? getPositionForNode(toNodeId) : undefined) ??
    (targetId && getPositionForPlayer ? getPositionForPlayer(targetId) : undefined);

  return {
    poolId: 'thunder_fist',
    args: {
      phase: event.type === 'item_cast' ? 'cast' : 'impact',
      casterPosition,
      targetPosition,
      direction,
      modelScene: event.type === 'item_cast' ? null : (getItemModelScene?.('thunder_fist') ?? null),
    },
  };
}
